import { useState, useRef, useEffect } from 'react';
import { ArrowRight, Zap, BarChart3, ShieldCheck, Loader2, LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';
import { ParticleBackground } from '@/components/ui/ParticleBackground';
import logoImage from '@/assets/zen-logo2.png';

// App branding constants
const APP_TAGLINE = 'AI-first analytics for humans';
const APP_DESCRIPTION = 'Talk to your Google Analytics & Search Console data in plain English';

// Toggle this to switch between layouts: 'horizontal' | 'vertical'
const FEATURE_LAYOUT: 'horizontal' | 'vertical' = 'horizontal';

interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  delay: number;
  layout?: 'horizontal' | 'vertical';
}

function FeatureCard({ title, description, icon: Icon, delay, layout = 'vertical' }: FeatureCardProps) {
  const isHorizontal = layout === 'horizontal';

  return (
    <div
      className={cn(
        "group relative rounded-2xl bg-[#0F1623]/60 border border-white/5 backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_30px_-10px_rgba(59,130,246,0.3)] overflow-hidden animate-slide-up select-none cursor-default",
        isHorizontal ? "px-6 py-5 flex-1" : "p-5"
      )}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
    >
      {/* Glow Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className={cn(
        "relative z-10",
        isHorizontal ? "flex flex-col items-center text-center gap-3" : "flex items-start gap-4"
      )}>
        <div className={cn(
          "flex-shrink-0 rounded-lg bg-[#1E293B]/50 text-primary group-hover:text-blue-300 group-hover:scale-110 transition-all duration-300 border border-white/5 ring-1 ring-white/5 shadow-inner group-hover:shadow-[0_0_15px_-3px_rgba(59,130,246,0.4)]",
          isHorizontal ? "p-2.5" : "p-3"
        )}>
          <Icon size={isHorizontal ? 20 : 22} strokeWidth={1.5} />
        </div>
        <div>
          <h3 className={cn(
            "font-medium text-slate-100 group-hover:text-white transition-colors",
            isHorizontal ? "text-sm mb-1" : "text-base mb-1"
          )}>
            {title}
          </h3>
          <p className={cn(
            "text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors",
            isHorizontal ? "text-xs" : "text-sm"
          )}>
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

const features = [
  {
    icon: Zap,
    title: 'Natural Language Queries',
    description: 'Ask questions in plain English and get instant answers without digging through reports.',
  },
  {
    icon: BarChart3,
    title: 'Unified Analytics',
    description: 'See Google Analytics and Search Console data in one cohesive dashboard.',
  },
  {
    icon: ShieldCheck,
    title: 'Privacy First',
    description: 'Your data stays secure. We only request read-only access to your analytics.',
  },
];

export function WelcomeScreen() {
  const { login, isLoading } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [isHoveringCta, setIsHoveringCta] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const blobRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });

      if (blobRef.current) {
        const moveX = (e.clientX - window.innerWidth / 2) * -0.02;
        const moveY = (e.clientY - window.innerHeight / 2) * -0.02;
        blobRef.current.style.transform = `translate(${moveX}px, ${moveY}px)`;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleLogin = async () => {
    setError(null);
    try {
      await login();
    } catch (err) {
      setError('Failed to connect. Please try again.');
      console.error('Login error:', err);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#050A14] flex flex-col items-center justify-center overflow-hidden selection:bg-blue-500/30">
      {/* Dynamic Flashlight Background Overlay */}
      <div
        className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-300"
        style={{
          background: `radial-gradient(800px circle at ${mousePos.x}px ${mousePos.y}px, rgba(29, 78, 216, 0.08), transparent 80%)`,
        }}
      />

      {/* Background Ambience with Parallax */}
      <div
        ref={blobRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none transition-transform duration-100 ease-out z-0"
      >
        <div className="absolute inset-0 bg-blue-900/20 rounded-full blur-[120px] animate-pulse-slow" />
      </div>

      {/* Gradient fade overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-[#050A14]/50 to-[#050A14] pointer-events-none z-10" />

      {/* Particle Background */}
      <ParticleBackground />

      {/* Main Content Container */}
      <div className={cn(
        "relative z-20 w-full px-6 py-12 flex flex-col items-center text-center",
        FEATURE_LAYOUT === 'horizontal' ? "max-w-4xl" : "max-w-xl"
      )}>
        {/* Logo Section */}
        <div className="mb-8 relative group animate-fade-in">
          <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 scale-150" />
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-b from-[#1E293B] to-[#0F1623] border border-white/10 flex items-center justify-center shadow-[0_0_40px_-10px_rgba(59,130,246,0.3)] group-hover:scale-105 transition-transform duration-500">
            <img
              src={logoImage}
              alt="ChartZen"
              className="w-[88px] h-[88px] object-contain drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]"
            />
          </div>
        </div>

        {/* Headlines */}
        <h1
          className="text-4xl md:text-5xl tracking-tight text-white mb-3 animate-slide-up drop-shadow-lg"
          style={{ animationDelay: '100ms', animationFillMode: 'both' }}
        >
          <span className="font-light">CHART</span>
          <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-slate-400">
            ZEN
          </span>
        </h1>

        <p
          className="text-primary font-medium mb-4 animate-slide-up tracking-wide"
          style={{ animationDelay: '200ms', animationFillMode: 'both' }}
        >
          {APP_TAGLINE}
        </p>

        <p
          className="text-slate-400 mb-10 max-w-md mx-auto leading-relaxed animate-slide-up"
          style={{ animationDelay: '300ms', animationFillMode: 'both' }}
        >
          {APP_DESCRIPTION}
        </p>

        {/* Feature Cards */}
        <div className={cn(
          "w-full mb-10",
          FEATURE_LAYOUT === 'horizontal'
            ? "flex gap-5"
            : "space-y-3 text-left"
        )}>
          {features.map((feature, i) => (
            <FeatureCard
              key={i}
              title={feature.title}
              description={feature.description}
              icon={feature.icon}
              delay={400 + i * 150}
              layout={FEATURE_LAYOUT}
            />
          ))}
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 rounded-lg bg-danger/10 border border-danger/20 px-4 py-2 text-sm text-danger animate-fade-in">
            {error}
          </div>
        )}

        {/* CTA Section */}
        <div
          className="flex flex-col items-center gap-4 animate-slide-up"
          style={{ animationDelay: '850ms', animationFillMode: 'both' }}
        >
          <button
            onClick={handleLogin}
            disabled={isLoading}
            onMouseEnter={() => setIsHoveringCta(true)}
            onMouseLeave={() => setIsHoveringCta(false)}
            className={cn(
              'group relative overflow-hidden rounded-full bg-primary hover:bg-primary-hover text-white px-8 py-4 font-medium transition-all duration-300',
              'shadow-[0_0_20px_-5px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_-5px_rgba(59,130,246,0.7)]',
              'hover:scale-[1.02] active:scale-[0.98]',
              'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100'
            )}
          >
            <div className="relative z-10 flex items-center gap-3">
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>Connect with Google</span>
                  <ArrowRight
                    size={18}
                    className={cn(
                      'transition-transform duration-300',
                      isHoveringCta && 'translate-x-1'
                    )}
                  />
                </>
              )}
            </div>

            {/* Button shimmer effect */}
            {!isLoading && (
              <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 group-hover:animate-shimmer" />
            )}
          </button>

          <p className="text-xs text-slate-500 text-center max-w-[300px]">
            We only request read-only access to your analytics. Your data is encrypted and secure.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 text-slate-600 text-xs z-20">
        &copy; {new Date().getFullYear()} ChartZen
      </div>
    </div>
  );
}
